import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useData } from "../context/DataContext";
import { calculateExpenseBreakdown } from "../utils/calculations";
import { getFontFamily } from "../utils/fontConfig";

const ExpenseChart: React.FC = () => {
  const { state } = useData();
  const { transactions } = state;

  const expenseData = useMemo(() => {
    const breakdown = calculateExpenseBreakdown(
      transactions,
      state.currentUserId || ""
    );

    // Filter out any items with invalid data
    return breakdown.filter(
      (item) =>
        typeof item.amount === "number" &&
        !isNaN(item.amount) &&
        isFinite(item.amount) &&
        item.amount > 0
    );
  }, [transactions, state.currentUserId]);

  // Add error handling for empty data
  if (!expenseData || expenseData.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Expense Breakdown</h3>
        </div>
        <div className="p-8 text-center text-gray-500">
          <p>No expense data available</p>
        </div>
      </div>
    );
  }

  const chartOptions = {
    series: expenseData.map((item) => item.amount),
    colors: [
      "#10b981", // Primary emerald
      "#059669", // Darker emerald
      "#ef4444", // Red
      "#f59e0b", // Amber
      "#8b5cf6", // Purple
      "#06b6d4", // Cyan
      "#f97316", // Orange
      "#ec4899", // Pink
      "#84cc16", // Lime
      "#6b7280", // Gray
    ],
    chart: {
      height: 320,
      width: "100%",
      type: "donut" as const,
      fontFamily: getFontFamily(),
    },
    stroke: {
      colors: ["transparent"],
      lineCap: "round" as const,
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
              fontFamily: getFontFamily(),
              offsetY: 20,
              fontSize: "14px",
              color: "var(--chart-text-color, #6b7280)",
            },
            total: {
              showAlways: true,
              show: true,
              label: "Total Expenses",
              fontFamily: getFontFamily(),
              fontSize: "14px",
              color: "var(--chart-text-color, #6b7280)",
              formatter: function (w: any) {
                const sum = w.globals.seriesTotals.reduce(
                  (a: number, b: number) => {
                    return a + b;
                  },
                  0
                );
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(sum);
              },
            },
            value: {
              show: true,
              fontFamily: getFontFamily(),
              offsetY: -20,
              fontSize: "24px",
              fontWeight: "bold",
              color: "var(--chart-text-color, #374151)",
              formatter: function (val: string) {
                const value = parseFloat(val);
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(value);
              },
            },
          },
          size: "80%",
        },
      },
    },
    grid: {
      padding: {
        top: -2,
      },
    },
    labels: expenseData.map((item) => item.category),
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: "bottom" as const,
      fontFamily: getFontFamily(),
      fontSize: "14px",
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 640,
        options: {
          chart: {
            height: 280,
          },
          legend: {
            position: "bottom",
            fontSize: "12px",
          },
        },
      },
    ],
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Expense Breakdown</h3>
      </div>

      <div className="p-4">
        {expenseData.length > 0 ? (
          <div className="relative">
            {(() => {
              try {
                return (
                  <ReactApexChart
                    options={chartOptions}
                    series={chartOptions.series}
                    type="donut"
                    height={320}
                  />
                );
              } catch (error) {
                console.error("Error rendering ExpenseChart:", error);
                return (
                  <div className="text-center text-gray-500 py-8">
                    <p>Error loading chart data</p>
                  </div>
                );
              }
            })()}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>No expense data available for chart</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseChart;
